import { Component, inject } from "@angular/core";
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import type { ScopedProject } from "@domain/project/project-guards";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { BusinessScope } from "@lib/constants/business-scope";
import { AreaHeaderComponent } from "@odx/angular/components/area-header";
import { ButtonComponent } from "@odx/angular/components/button";
import { FormDirective, FormFieldComponent, FormFieldControlDirective } from "@odx/angular/components/form-field";
import { ModalRef } from "@odx/angular/components/modal";
import { PdfService } from "@pdf/pdf.service";
import { ToastHelper } from "@utils/toast-helper.service";
import { switchMap, tap } from "rxjs";

@Component({
  selector: "app-project-version-modal",
  imports: [
    AreaHeaderComponent,
    FormDirective,
    FormsModule,
    FormFieldComponent,
    FormFieldControlDirective,
    ReactiveFormsModule,
    ButtonComponent,
    UiKitModule,
  ],
  templateUrl: "./project-version-modal.component.html",
  styleUrl: "./project-version-modal.component.scss",
})
export class ProjectVersionModalComponent {
  private formBuilder = inject(FormBuilder);
  private pdfService = inject(PdfService);
  private projectService = inject(ProjectService);
  private toastHelper = inject(ToastHelper);
  private userService = inject(UserService);

  public readonly modalRef = inject(ModalRef<any, string>);
  projectVersionForm!: FormGroup;
  project!: ScopedProject;
  isRiskAssessmentIncluded: boolean = false;
  isSignOffPageIncluded: boolean = true;
  isMonitoringScope: boolean = false;

  constructor() {
    this.project = this.modalRef.data.project;
    this.projectVersionForm = this.formBuilder.group({
      version: [this.project.version],
    });
    this.userService.businessScope$.subscribe((scope) => {
      this.isMonitoringScope = scope === BusinessScope.MONITORING;
    });
  }

  onConfirm() {
    this.toastHelper.message(
      $localize`:@@toast.downloadProjectStarted: PDF Export gestartet`,
      $localize`:@@toast.DownloadProjectDescription: Das Projekt ${this.project.name} wird als PDF exportiert.`,
    );
    this.project.version = this.projectVersionForm.controls["version"].value;
    this.exportToPdf().subscribe({
      next: () =>
        this.toastHelper.success(
          $localize`:@@toast.ProjectSuccessfulDownloaded:PDF Export erfolgreich`,
          $localize`:@@toast.ProjectSuccessfulDownloadDescription:Das Projekt ${this.project.name} wurde erfolgreich als PDF exportiert.`,
        ),
      error: () => {
        this.toastHelper.error(
          $localize`:@@toast.ProjectSuccessfulDownloadFailed:PDF Export fehlgeschlagen`,
          $localize`:@@toast.ProjectSuccessfulDownloadFailDescription:Das Projekt ${this.project.name} konnte nicht exportiert werden.`,
        );
      },
      complete: () => this.modalRef.close(true),
    });
  }

  exportToPdf() {
    return this.pdfService.exportProjectToPdf(this.project, this.isRiskAssessmentIncluded, this.isSignOffPageIncluded).pipe(
      tap(({ pdf, fileName }) => {
        pdf.save(fileName);
      }),
      switchMap(() => this.projectService.update(this.project)),
    );
  }
}
